<template>
	<AuthLayout>
		<template #eyebrow>Sign in to platform</template>
		<template #headline>Welcome back<br/>to <span class="accent">{{ appName }}.</span></template>
		<template #lead>Sign in to manage your DOOH campaigns, screens and real-time analytics.</template>

		<!-- 2FA view -->
		<template v-if="twoFactorAuth">
			<div class="auth-card__head">
				<p class="auth-card__step">Verification</p>
				<h1 class="auth-card__title">Two-Factor Authentication</h1>
				<p class="auth-card__subtitle">Enter the verification code from your authenticator app</p>
			</div>

			<form class="auth-form" @submit.prevent="check2fa">
				<div class="auth-field">
					<label class="auth-field__label">Verification Code</label>
					<input
						v-model="twoFactorCode"
						type="text"
						class="auth-input-light auth-2fa-input"
						placeholder="000000"
						maxlength="6"
						autocomplete="one-time-code"
					/>
				</div>

				<div v-if="wrongCode" class="auth-error">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
					{{ $t('errors.wrong_code') || 'Invalid verification code' }}
				</div>

				<button type="submit" class="auth-btn">
					Verify
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
				</button>

				<button type="button" class="auth-btn auth-btn--outline" @click="twoFactorAuth = false">
					&larr; Back
				</button>
			</form>
		</template>

		<!-- Login view -->
		<template v-else>
			<div class="auth-card__head">
				<p class="auth-card__step">01 — Sign in</p>
				<h1 class="auth-card__title">Sign in to your account</h1>
				<p class="auth-card__subtitle">Use the email and password of your {{ appName }} account</p>
			</div>

			<form class="auth-form" autocomplete="off" @submit.prevent="loginRequest">
				<div class="auth-field">
					<label class="auth-field__label" for="login-email">Email</label>
					<div class="auth-input-wrap">
						<input
							id="login-email"
							v-model="email"
							type="email"
							class="auth-input-light"
							placeholder="you@company.com"
							autocomplete="email"
						/>
					</div>
					<p v-if="wrongEmail" class="auth-field-error">{{ wrongEmail }}</p>
				</div>

				<div class="auth-field">
					<div class="auth-field__row">
						<label class="auth-field__label" style="margin-bottom:0" for="login-password">Password</label>
						<router-link :to="{ name: 'v2-reset-password' }" class="auth-field__action">Forgot?</router-link>
					</div>
					<div class="auth-input-wrap">
						<input
							id="login-password"
							v-model="password"
							:type="showPassword ? 'text' : 'password'"
							class="auth-input-light auth-input-light--with-icon"
							placeholder="••••••••"
							autocomplete="current-password"
						/>
						<button type="button" class="auth-input__icon-btn" @click="togglePassword">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
								<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
							</svg>
						</button>
					</div>
				</div>

				<div v-if="wrongCredentials" class="auth-error">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
					{{ $t('errors.wrong_credentials') }}
				</div>

				<button type="submit" class="auth-btn">
					Sign in
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
				</button>
			</form>

			<div v-if="$isWhitelabel" class="auth-card__foot">
				<p>Don't have an account? <router-link :to="{ name: 'v2-registration' }">Create one</router-link></p>
			</div>
		</template>
	</AuthLayout>
</template>


<script>
import PageLogin from '@/pages/login.vue';
import AuthLayout from './auth-layout.vue';

export default {
	name: 'V2PageLogin',

	extends: PageLogin,

	components: {
		...PageLogin.components,
		AuthLayout,
	},

	computed: {
		appName() {
			return (this.$uioverrides && this.$uioverrides.name) || 'DISPL Ads';
		},
	},

	methods: {
		// Override parent's checkAgency to redirect to V2 routes
		async checkAgency() {
			const { requestAgencies } = await import('@/api/agency');
			const hasNextRoute = this.$route.query && this.$route.query.next;
			let agencies = await requestAgencies();

			if (agencies.length > 1) {
				this.$set(this, 'agencies', agencies);
				this.$router.push({
					name: 'v2-choose-agency',
					query: { next: hasNextRoute ? this.$route.query.next : '' },
				});
				return;
			}

			this.setAgency(agencies[0].id);
			await this.getProfile();
			this.$router.push(hasNextRoute ? { path: this.$route.query.next } : { name: 'v2-index' });
		},
	},
};
</script>


<style lang="sass" scoped>
@import './auth-form-styles'

.auth-2fa-input
	text-align: center
	font-size: 24px
	letter-spacing: 0.3em
	font-weight: 600

.auth-btn--outline
	background: transparent
	border: 1px solid rgba($border, 0.6)
	color: $muted
	box-shadow: none

	&:hover
		background: rgba($border, 0.3)
		transform: none

.auth-field-error
	margin-top: 6px
	font-size: 12px
	color: $red
</style>
